/**
 * `W5-T12` §4.10 — what charge to ask looks like on the client's pages: the price and the promise on
 * the publish step, the button that pays, the way back from Checkout, the window panel and the
 * auction's line on the job's page.
 *
 * Every amount is the API's integer cents through `formatMoney` (charter rule 2), and every number of
 * quotes is said as *up to* (AC 41). Nothing here is an error: a refund, a slow webhook and a payment
 * service that is down are states of the job, so they are `role="status"`, never an alert.
 */
import { type ReactElement } from 'react';
import { Form } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@marketplace/ui';
import { type Job, type JobAsk, type QuoteWindowView } from '@marketplace/contracts';
import { formatPeriodEnd } from '../../billing/billing.js';
import { formatMoney } from '../money.js';
import { ASK_UNAVAILABLE, type AskCharging, type AskReturn } from './ask.js';
import { useAskReturnWatch } from './watch.js';

export type AskMode = 'QUOTES' | 'AUCTION';

const euros = (amountCents: number, locale: string): string =>
  formatMoney({ amountCents, currency: 'EUR' }, locale);

/** The price that applies to the mode the client chose. */
function priceOf(prices: AskCharging, mode: AskMode) {
  return mode === 'AUCTION' ? prices.auction : prices.quoteWindow;
}

/** The publish button's label: *Publicar · 2,00 €* or *Abrir la subasta · 20,00 €*. */
export function useAskCta(prices: AskCharging, mode: AskMode, locale: string): string {
  const { t } = useTranslation();
  const price = euros(priceOf(prices, mode).amountCents, locale);
  return mode === 'AUCTION'
    ? t('ask.publish.auction.cta', { price })
    : t('ask.publish.quotes.cta', { price });
}

/** The price, with *IVA incluido* or *+ IVA* (E7), and what it buys. */
export function AskPublishTerms({
  prices,
  mode,
  locale,
}: {
  prices: AskCharging;
  mode: AskMode;
  locale: string;
}): ReactElement {
  const { t } = useTranslation();
  const chosen = priceOf(prices, mode);
  const price = euros(chosen.amountCents, locale);
  const tax = t(chosen.taxInclusive ? 'ask.price.taxIncluded' : 'ask.price.taxExcluded');

  return (
    <div data-testid="ask-publish-terms">
      <p className="mp-lead" data-testid="ask-price">
        {`${price} · ${tax}`}
      </p>
      <p className="mp-meta" data-testid="ask-promise">
        {mode === 'AUCTION'
          ? t('ask.publish.auction.promise', { price })
          : t('ask.publish.quotes.promise', {
              size: prices.quoteWindow.size,
              days: prices.quoteWindow.days,
            })}
      </p>
    </div>
  );
}

/** §4.1, §7: charging is on and cannot be taken now. The draft is untouched. */
export function AskUnavailable(): ReactElement {
  const { t } = useTranslation();
  return (
    <p className="mp-lead" role="status" data-testid="ask-unavailable">
      {t('ask.unavailable')}
    </p>
  );
}

/** A write's refusal: `ask.unavailable` as the state it is, anything else as the error it is. */
function AskRefusal({ error }: { error: string | null }): ReactElement | null {
  const { t } = useTranslation();
  if (error === null) return null;
  return error === ASK_UNAVAILABLE ? <AskUnavailable /> : <Alert tone="error">{t(error)}</Alert>;
}

/** The draft's page, with charging on: what publishing costs, and the button that pays (§4.3). */
export function AskDraftPublish({
  prices,
  locale,
  busy,
  error,
}: {
  prices: AskCharging;
  locale: string;
  busy: boolean;
  error: string | null;
}): ReactElement {
  const cta = useAskCta(prices, 'QUOTES', locale);
  return (
    <Form method="post" data-testid="ask-publish">
      <input type="hidden" name="intent" value="ask-publish" />
      <AskPublishTerms prices={prices} mode="QUOTES" locale={locale} />
      <Button type="submit" variant="primary" isDisabled={busy}>
        {cta}
      </Button>
      <AskRefusal error={error} />
    </Form>
  );
}

/**
 * Back from Checkout (§4.10). `?ask=paid` on a draft: Stripe is confirming, and the job is read again
 * until the webhook publishes it; after a minute, the payment is slow. `?ask=cancelled` on a draft:
 * nothing was charged. On a published job, nothing: the page is the answer.
 */
export function AskReturnNotice({
  returned,
  jobId,
  draft,
}: {
  returned: AskReturn | null;
  jobId: string;
  draft: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const watching = returned === 'paid' && draft;
  const open = useAskReturnWatch(watching, jobId);
  if (!draft || returned === null) return null;

  const key =
    returned === 'cancelled'
      ? 'ask.return.cancelled'
      : open
        ? 'ask.return.confirming'
        : 'ask.return.slow';
  return (
    <p className="mp-lead" role="status" data-testid="ask-return">
      {t(key)}
    </p>
  );
}

/** Statuses on which the client's job page shows its windows (§4.10). */
const WINDOWED: readonly Job['status'][] = ['OPEN', 'AWARDED', 'CANCELLED'];

/** One window's line, by `status`, `drew` and `full` (§4.10). */
function windowLine(
  bought: QuoteWindowView,
  locale: string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  const price = euros(bought.payment.amountCents, locale);
  if (bought.status === 'EMPTY') {
    if (bought.payment.status === 'REFUNDED') return t('ask.window.refunded', { price });
    if (bought.payment.status === 'REFUND_FAILED') return t('ask.window.refund_failed', { price });
    return t('ask.window.refund_pending', { price });
  }
  if (bought.full) return t('ask.window.full');
  if (bought.drew === 0 && bought.status === 'OPEN') {
    return t('ask.window.waiting', { date: formatPeriodEnd(bought.refundDeadlineAt, locale) });
  }
  return t('ask.window.drawing', { drew: bought.drew, size: bought.size });
}

/**
 * What the client paid for on a published job: one line per window and, when another may be bought,
 * the button that asks for it (§4.4, no body); or the auction's one line.
 */
export function AskPaidFor({
  job,
  ask,
  prices,
  locale,
  busy,
  error,
}: {
  job: Job;
  ask: JobAsk | null;
  prices: AskCharging | null;
  locale: string;
  busy: boolean;
  error: string | null;
}): ReactElement | null {
  const { t } = useTranslation();
  if (ask === null) return null;

  if (job.mode === 'AUCTION') {
    return ask.auction?.status === 'PAID' ? (
      <p className="mp-meta" data-testid="ask-auction">
        {t('ask.auction.paid', { price: euros(ask.auction.amountCents, locale) })}
      </p>
    ) : null;
  }

  const buy = ask.canBuyWindow && prices !== null ? prices.quoteWindow : null;
  if (!WINDOWED.includes(job.status) || (ask.windows.length === 0 && buy === null)) return null;

  return (
    <section className="mp-section" data-testid="ask-windows">
      <ul className="mp-list">
        {ask.windows.map((bought) => (
          <li key={bought.seq} data-testid={`ask-window-${bought.seq}`}>
            {windowLine(bought, locale, t)}
          </li>
        ))}
      </ul>
      {buy === null ? null : (
        <Form method="post">
          <input type="hidden" name="intent" value="ask-window" />
          <Button type="submit" variant="secondary" isDisabled={busy}>
            {t('ask.buyMore.cta', { size: buy.size, price: euros(buy.amountCents, locale) })}
          </Button>
        </Form>
      )}
      <AskRefusal error={error} />
    </section>
  );
}
